import React, { useState, useRef, useCallback } from "react";
import { Stage, Layer, Image as KonvaImage, Transformer, Rect, Path, Ellipse, Line, Text } from "react-konva";
import useImage from "use-image";
import SpeechBubble from "./SpeechBubble";
import TextElement from "./TextElement";
import ShapeElement from "./ShapeElement";
import AssetPanel from "./AssetPanel";
import PropertiesPanel from "./PropertiesPanel";
import Toolbar from "./Toolbar";
import DrawingSettings from "./DrawingSettings";
import ImageUploader from "./ImageUploader";
import "./App.css";

// Path utilities (module scope so both components and App can use them)
const applySmoothing = (points, smoothness) => {
  if (!Array.isArray(points) || points.length < 3) return points || [];

  const smoothed = [points[0]];
  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const next = points[i + 1];
    const smoothX = curr[0] + smoothness * ((prev[0] + next[0]) / 2 - curr[0]);
    const smoothY = curr[1] + smoothness * ((prev[1] + next[1]) / 2 - curr[1]);
    smoothed.push([smoothX, smoothY]);
  }
  smoothed.push(points[points.length - 1]);
  return smoothed;
};

const generateSmoothPath = (points, smoothness) => {
  if (!Array.isArray(points) || points.length === 0) return '';
  if (points.length === 1) {
    const [x, y] = points[0];
    return `M ${x} ${y} L ${x + 0.01} ${y + 0.01}`;
  }
  const smoothed = smoothness > 0 ? applySmoothing(points, smoothness) : points;
  return smoothed.reduce((path, point, i) => {
    if (i === 0) return `M ${point[0]} ${point[1]}`;
    return `${path} L ${point[0]} ${point[1]}`;
  }, '');
};

// (deprecated) PANEL_TEMPLATES removed; pages are added before/after current

const StrokeElement = ({ id, pathData, strokeColor, strokeWidth, opacity, isSelected, onSelect, onChange, isDraggable = true, points = [], smoothness = 0 }) => {
  const pathRef = useRef();
  const trRef = useRef();

  React.useEffect(() => {
    if (isSelected && pathRef.current && trRef.current) {
      trRef.current.nodes([pathRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  const handleTransform = () => {
    if (!pathRef.current) return;
    const node = pathRef.current;

    const srcPoints = Array.isArray(points) ? points : [];
    if (srcPoints.length === 0) {
      node.position({ x: 0, y: 0 });
      node.scaleX(1);
      node.scaleY(1);
      return;
    }

    const xs = srcPoints.map(p => p[0]);
    const ys = srcPoints.map(p => p[1]);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const srcW = Math.max(0.0001, maxX - minX);
    const srcH = Math.max(0.0001, maxY - minY);
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;

    // Target bounding box after current transform
    const rect = node.getClientRect({ skipShadow: true, skipStroke: true });
    const dstW = Math.max(0.0001, rect.width);
    const dstH = Math.max(0.0001, rect.height);
    const scaleX = dstW / srcW;
    const scaleY = dstH / srcH;

    // Compute translation so that min point maps exactly to rect.x/y
    const mappedMinX = cx + (minX - cx) * scaleX;
    const mappedMinY = cy + (minY - cy) * scaleY;
    const dX = rect.x - mappedMinX;
    const dY = rect.y - mappedMinY;

    const transformed = srcPoints.map(([x, y]) => [
      cx + (x - cx) * scaleX + dX,
      cy + (y - cy) * scaleY + dY
    ]);

    // Reset node transforms so subsequent transforms start fresh
    node.position({ x: 0, y: 0 });
    node.scaleX(1);
    node.scaleY(1);

    const newPath = generateSmoothPath(transformed, smoothness);
    onChange({ points: transformed, pathData: newPath });
  };

  return (
    <>
      <Path
        ref={pathRef}
        id={id}
        data={pathData}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        opacity={opacity}
        lineCap="round"
        lineJoin="round"
        draggable={isDraggable}
        onClick={onSelect}
        onTap={onSelect}
        onDragEnd={(e) => {
          const tx = e.target.x() || 0;
          const ty = e.target.y() || 0;
          const srcPoints = Array.isArray(points) ? points : [];
          const shifted = srcPoints.map(([x, y]) => [x + tx, y + ty]);
          // reset node position
          e.target.position({ x: 0, y: 0 });
          const newPath = generateSmoothPath(shifted, smoothness);
          onChange({ points: shifted, pathData: newPath });
        }}
        onTransformEnd={handleTransform}
      />
      {isSelected && (
        <Transformer
          ref={trRef}
          rotateEnabled={false}
          enabledAnchors={["top-left", "top-right", "bottom-left", "bottom-right"]}
          keepRatio={false}
          boundBoxFunc={(oldBox, newBox) => {
            // Prevent negative sizes
            const minSize = 5;
            if (newBox.width < 0 || newBox.height < 0) return oldBox;
            const w = Math.max(minSize, newBox.width);
            const h = Math.max(minSize, newBox.height);
            return { ...newBox, width: w, height: h };
          }}
        />
      )}
    </>
  );
};

// Placement preview component
const AssetPlacementPreview = ({ src, scaling = 50, x, y, canvasHeight = 600 }) => {
  const [image] = useImage(src);

  if (!image) {
    const size = 100;
    return (
      <Rect
        x={x - size / 2}
        y={y - size / 2}
        width={size}
        height={size}
        fill="#e0e0e0"
        stroke="#000000"
        strokeWidth={2}
        dash={[5, 5]}
        opacity={0.7}
        listening={false}
      />
    );
  }

  const naturalWidth = image.width || 100;
  const naturalHeight = image.height || 100;
  const targetHeight = (canvasHeight * scaling) / 100;
  const scale = targetHeight / naturalHeight;
  const renderWidth = naturalWidth * scale;
  const renderHeight = naturalHeight * scale;

  return (
    <KonvaImage
      image={image}
      x={x - renderWidth / 2}
      y={y - renderHeight / 2}
      width={renderWidth}
      height={renderHeight}
      opacity={0.8}
      listening={false}
    />
  );
};

const PlacementPreview = ({ mode, data, x, y, canvasDimensions }) => {
  if (!mode) return null;

  const commonProps = {
    x: x,
    y: y,
    opacity: 0.7,
    listening: false, // Don't interfere with mouse events
  };

  switch (mode) {
    case 'asset':
      if (data?.type === 'speechBubble') {
        const bw = 160;
        const bh = 100;
        return (
          <Rect
            {...commonProps}
            x={x - bw / 2}
            y={y - bh / 2}
            width={bw}
            height={bh}
            fill="#ffffff"
            stroke="#000000"
            strokeWidth={2}
            cornerRadius={10}
          />
        );
      } else if (data?.src) {
        return (
          <AssetPlacementPreview
            src={data.src}
            scaling={data.scaling || 50}
            x={x}
            y={y}
            canvasHeight={canvasDimensions?.height || 600}
          />
        );
      }
      break;

    case 'shape':
      if (data === 'rectangle') {
        return (
          <Rect
            {...commonProps}
            x={x - 40}
            y={y - 40}
            width={80}
            height={80}
            fill="#ffffff"
            stroke="#000000"
            strokeWidth={2}
          />
        );
      } else if (data === 'circle') {
        return (
          <Ellipse
            {...commonProps}
            x={x}
            y={y}
            radiusX={40}
            radiusY={40}
            fill="#ffffff"
            stroke="#000000"
            strokeWidth={2}
          />
        );
      } else if (data === 'line') {
        return (
          <Line
            {...commonProps}
            x={x}
            y={y}
            points={[-50, 0, 50, 0]}
            stroke="#000000"
            strokeWidth={2}
          />
        );
      }
      break;

    case 'text':
      return (
        <Text
          {...commonProps}
          x={x - 50}
          y={y - 12}
          text="New Text"
          fontFamily="Comic Sans MS"
          fontSize={24}
          fill="#000000"
        />
      );

    default:
      return null;
  }

  return null;
};

const Asset = ({ id, src, x, y, width, height, flipped, scaleX, isSelected, onSelect, onChange, scaling = 50, canvasHeight = 600, type = "asset", rotation = 0, centered = false }) => {
  const [image] = useImage(src);
  const [mirroredImage, setMirroredImage] = React.useState(null);
  const imageRef = useRef();
  const trRef = useRef();

  React.useEffect(() => {
    if (isSelected && imageRef.current && trRef.current) {
      trRef.current.nodes([imageRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  // Build a mirrored bitmap once when the image loads so we never use negative scales
  React.useEffect(() => {
    if (!image) return;
    const w = image.width || 1;
    const h = image.height || 1;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(image, 0, 0);
    const mirrored = new window.Image();
    mirrored.onload = () => setMirroredImage(mirrored);
    mirrored.src = canvas.toDataURL();
  }, [image]);

  const handleTransform = () => {
    if (!imageRef.current) return;
    const node = imageRef.current;
    const scaleXAbs = Math.abs(node.scaleX());
    const scaleYAbs = Math.abs(node.scaleY());
    const rotation = node.rotation();

    const newWidth = Math.max(5, node.width() * scaleXAbs);
    const newHeight = Math.max(5, node.height() * scaleYAbs);

    // Reset local scale for stability (sizes captured in width/height)
    node.scaleX(1);
    node.scaleY(1);

    if (type === "uploadedImage") {
      onChange({ x: node.x(), y: node.y(), width: newWidth, height: newHeight, rotation });
    } else {
      onChange({ x: node.x(), y: node.y(), width: newWidth, height: newHeight });
    }
  };

  // Prefer dimensions from state; fall back to natural scaled to appropriate height
  const naturalWidth = image ? image.width : (width || 100);
  const naturalHeight = image ? image.height : (height || 100);

  let renderWidth = width;
  let renderHeight = height;

  if (!width || !height) {
    // Scale based on percentage of canvas height
    const targetHeight = (canvasHeight * scaling) / 100;
    const scale = targetHeight / naturalHeight;
    renderWidth = naturalWidth * scale;
    renderHeight = naturalHeight * scale;
  }

  // Sync initial computed dimensions back to state so Properties panel shows accurate numbers
  React.useEffect(() => {
    if (!image) return;
    if (!width || !height) {
      onChange({ width: renderWidth, height: renderHeight });
    }
  }, [image, width, height, renderWidth, renderHeight, onChange]);

  // Choose image: mirrored when flipped, otherwise normal
  const displayImage = flipped && mirroredImage ? mirroredImage : image;

  // Use centered anchor via offset so flips/rotations occur about center
  const centerX = centered ? x : x + (renderWidth || 0) / 2;
  const centerY = centered ? y : y + (renderHeight || 0) / 2;

  return (
    <>
      <KonvaImage
        ref={imageRef}
        id={id}
        image={displayImage}
        x={centerX}
        y={centerY}
        width={renderWidth}
        height={renderHeight}
        offsetX={renderWidth / 2}
        offsetY={renderHeight / 2}
        rotation={type === "uploadedImage" ? rotation : 0}
        draggable
        onClick={onSelect}
        onTap={onSelect}
        onDragEnd={(e) => {
          const cx = e.target.x();
          const cy = e.target.y();
          if (centered) {
            onChange({ x: cx, y: cy });
          } else {
            onChange({ x: cx - renderWidth / 2, y: cy - renderHeight / 2 });
          }
        }}
        onTransformEnd={handleTransform}
      />
      {isSelected && (
        <Transformer
          ref={trRef}
          rotateEnabled={type === "uploadedImage"}
          enabledAnchors={type === "uploadedImage" 
            ? ["top-left", "top-right", "bottom-left", "bottom-right", "top-center", "bottom-center", "middle-left", "middle-right"]
            : ["top-left", "top-right", "bottom-left", "bottom-right"]
          }
          keepRatio={type !== "uploadedImage"}
          boundBoxFunc={(oldBox, newBox) => {
            // Prevent negative sizes and enforce minimums
            const minSize = 5;
            if (newBox.width < 0 || newBox.height < 0) return oldBox;
            const w = Math.max(minSize, newBox.width);
            const h = Math.max(minSize, newBox.height);
            return { ...newBox, width: w, height: h };
          }}
        />
      )}
    </>
  );
};

export default function App() {
  const [selectedId, setSelectedId] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [speechBubbles, setSpeechBubbles] = useState([]);
  const [textElements, setTextElements] = useState([]);
  
  // Canvas dimension state
  const [canvasDimensions, setCanvasDimensions] = useState({
    width: 600,
    height: 600
  });

  // Shape state
  const [shapes, setShapes] = useState([]);

  // Drawing state
  const [drawingMode, setDrawingMode] = useState(false);
  const [strokes, setStrokes] = useState([]);
  const [drawingSettings, setDrawingSettings] = useState({
    strokeWidth: 3,
    strokeColor: '#000000',
    smoothness: 0.5,
    opacity: 1.0
  });
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentStroke, setCurrentStroke] = useState(null);
  const [drawingPoints, setDrawingPoints] = useState([]);

  // Image upload state
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Mouse-to-place state
  const [placementMode, setPlacementMode] = useState(null); // null, 'asset', 'shape', 'text'
  const [placementData, setPlacementData] = useState(null); // Data for the object being placed
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Panel state - each panel is a separate "page"
  const [panels, setPanels] = useState([]);
  const [currentPanelIndex, setCurrentPanelIndex] = useState(0);
  const [viewMode, setViewMode] = useState('panel'); // 'panel' or 'strip'
  const [stripRows, setStripRows] = useState(1);

  // Listen for strip row changes from Toolbar select without prop drilling deeply
  React.useEffect(() => {
    const onRows = (e) => setStripRows(Math.max(1, e.detail || 1));
    window.addEventListener('stripRowsChange', onRows);
    return () => window.removeEventListener('stripRowsChange', onRows);
  }, []);
  const [duplicateOnAdd, setDuplicateOnAdd] = useState(false);

  // Panel helpers (defined early so hooks below can reference them safely)
  const getCurrentPanelContent = () => {
    if (panels.length === 0) return { nodes: [], speechBubbles: [], textElements: [], shapes: [], strokes: [] };
    return panels[currentPanelIndex] || { nodes: [], speechBubbles: [], textElements: [], shapes: [], strokes: [] };
  };

  const updateCurrentPanelContent = useCallback((contentType, newContent) => {
    setPanels(prev => {
      if (prev.length === 0) return prev;
      return prev.map((panel, index) => (
        index === currentPanelIndex ? { ...panel, [contentType]: newContent } : panel
      ));
    });
  }, [currentPanelIndex, setPanels]);

  const stageRef = useRef();
  const canvasContainerRef = useRef(null);
  const [isPanning, setIsPanning] = useState(false);
  const panStateRef = useRef({ startX: 0, startY: 0, startScrollLeft: 0, startScrollTop: 0 });

  const handleStripPanDown = (e) => {
    if (viewMode !== 'strip') return;
    const pt = e?.evt;
    if (!pt || !canvasContainerRef.current) return;
    setIsPanning(true);
    panStateRef.current.startX = pt.clientX;
    panStateRef.current.startY = pt.clientY;
    panStateRef.current.startScrollLeft = canvasContainerRef.current.scrollLeft;
    panStateRef.current.startScrollTop = canvasContainerRef.current.scrollTop;
    e.evt.preventDefault();
  };

  const handleStripPanMove = (e) => {
    if (viewMode !== 'strip' || !isPanning) return;
    const pt = e?.evt;
    if (!pt || !canvasContainerRef.current) return;
    
    // Calculate movement delta from starting point
    const dx = pt.clientX - panStateRef.current.startX;
    const dy = pt.clientY - panStateRef.current.startY;
    
    // Apply absolute 1:1 scroll relative to start
    canvasContainerRef.current.scrollLeft = panStateRef.current.startScrollLeft - dx;
    canvasContainerRef.current.scrollTop = panStateRef.current.startScrollTop - dy;
    
    e.evt.preventDefault();
  };

  const handleStripPanUp = () => {
    setIsPanning(false);
  };

  // Resolve selection from the current panel's content (panel view)
  const selectedElement = React.useMemo(() => {
    if (!selectedId) return null;
    // Prefer current panel content
    const current = panels[currentPanelIndex] || { nodes: [], speechBubbles: [], textElements: [], shapes: [], strokes: [] };
    const inCurrent = [...(current.nodes || []), ...(current.speechBubbles || []), ...(current.textElements || []), ...(current.shapes || []), ...(current.strokes || [])]
      .find(el => el.id === selectedId);
    if (inCurrent) return inCurrent;
    // Fallback: search all panels (e.g., in strip view)
    const all = panels.flatMap(p => [
      ...(p.nodes || []),
      ...(p.speechBubbles || []),
      ...(p.textElements || []),
      ...(p.shapes || []),
      ...(p.strokes || [])
    ]);
    return all.find(el => el.id === selectedId) || null;
  }, [selectedId, panels, currentPanelIndex]);

  const handleDeleteElement = useCallback(() => {
    if (!selectedElement) return;
    const current = panels[currentPanelIndex] || { nodes: [], speechBubbles: [], textElements: [], shapes: [], strokes: [] };
    if (selectedElement.type === "speechBubble") {
      updateCurrentPanelContent('speechBubbles', (current.speechBubbles || []).filter(b => b.id !== selectedElement.id));
    } else if (selectedElement.type === "text") {
      updateCurrentPanelContent('textElements', (current.textElements || []).filter(t => t.id !== selectedElement.id));
    } else if (selectedElement.type === "shape") {
      updateCurrentPanelContent('shapes', (current.shapes || []).filter(s => s.id !== selectedElement.id));
    } else if (selectedElement.type === "drawing") {
      updateCurrentPanelContent('strokes', (current.strokes || []).filter(s => s.id !== selectedElement.id));
    } else {
      updateCurrentPanelContent('nodes', (current.nodes || []).filter(n => n.id !== selectedElement.id));
    }
    setSelectedId(null);
  }, [selectedElement, currentPanelIndex, panels, updateCurrentPanelContent]);

  // Keyboard event listener for delete key
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      // Handle Delete and Backspace keys
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedElement) {
        // Check if user is currently editing text (input, textarea, or contenteditable)
        const activeElement = document.activeElement;
        const isEditingText = activeElement && (
          activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.contentEditable === 'true' ||
          activeElement.isContentEditable
        );
        
        // Only delete element if NOT editing text
        if (!isEditingText) {
          e.preventDefault(); // Prevent default browser behavior
          handleDeleteElement();
        }
        // If editing text, let the default behavior happen (delete characters)
      }
    };

    // Add event listener to document
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup on unmount
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedElement, handleDeleteElement]); // Re-run when selectedElement or handleDeleteElement changes

  const handleAddAsset = (asset) => {
    if (panels.length === 0) {
      alert('Please create a panel layout first using the "Add Panels" dropdown.');
      return;
    }

    // Enter placement mode for assets
    setPlacementMode('asset');
    setPlacementData(asset);
  };

  // Add asset at specific position
  const handleAddAssetAtPosition = (asset, x, y) => {
    if (panels.length === 0) {
      alert('Please create a panel layout first using the "Add Panels" dropdown.');
      return;
    }

    const currentContent = getCurrentPanelContent();
    
    if (asset.type === "speechBubble") {
      // Create a new speech bubble
      const newId = `bubble_${Date.now()}`;
      const bw = 160;
      const bh = 100;
      const newBubble = {
        id: newId,
        x: x - bw / 2, // Center the bubble
        y: y - bh / 2,
        text: "New text!",
        fontFamily: "Comic Sans MS",
        fontSize: 16,
        fontColor: "#000000",
        width: bw,
        height: bh,
        tailBaseX: bw / 2,
        tailTipX: bw / 2 - 10,
        tailTipY: bh + 40,
        flipped: false,
        type: "speechBubble"
      };
      updateCurrentPanelContent('speechBubbles', [...currentContent.speechBubbles, newBubble]);
    } else {
      // Create a new asset using center coordinates; Asset component will honor centered flag
      const newId = `${asset.id}_${Date.now()}`;
      const newNode = {
        id: newId,
        src: asset.src,
        x: x,
        y: y,
        type: "asset",
        scaling: asset.scaling || 50,
        centered: true
      };
      updateCurrentPanelContent('nodes', [...currentContent.nodes, newNode]);
    }
  };

  // Image upload handlers
  const handleUploadImage = () => {
    setShowUploadModal(true);
  };

  const handleImageUpload = (imageAsset) => {
    // Add to current panel as a new asset
    if (panels.length === 0) {
      alert('Please create a panel layout first using the "Add Panels" dropdown.');
      return;
    }

    const currentContent = getCurrentPanelContent();
    const newId = `${imageAsset.id}_${Date.now()}`;
    const newNode = {
      id: newId,
      src: imageAsset.src,
      x: 100,
      y: 100,
      type: "uploadedImage",
      scaling: imageAsset.scaling || 100, // Use the scaling from the processed image
      width: imageAsset.width,
      height: imageAsset.height,
      rotation: 0,
    };
    updateCurrentPanelContent('nodes', [...currentContent.nodes, newNode]);
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
  };

  const handleAddText = () => {
    // Enter placement mode for text
    setPlacementMode('text');
    setPlacementData(null);
  };

  // Add text at specific position
  const handleAddTextAtPosition = (x, y) => {
    const current = getCurrentPanelContent();
    const newText = {
      id: `text-${Date.now()}`,
      x: x - 50, // Offset to center the text
      y: y - 12, // Offset to center vertically
      text: "New Text",
      fontFamily: "Comic Sans MS",
      fontSize: 24,
      fontColor: "#000000",
      type: "text"
    };
    updateCurrentPanelContent('textElements', [...(current.textElements || []), newText]);
    setSelectedId(newText.id);
  };

  const handleAddShape = (shapeType) => {
    // Enter placement mode for shapes
    setPlacementMode('shape');
    setPlacementData(shapeType);
  };

  // Add shape at specific position
  const handleAddShapeAtPosition = (shapeType, x, y) => {
    const current = getCurrentPanelContent();
    const width = shapeType === 'line' ? 100 : 80;
    const height = shapeType === 'line' ? 2 : 80;
    
    let shapeX, shapeY;
    if (shapeType === 'circle') {
      // For circles, pass center coordinates directly
      shapeX = x;
      shapeY = y;
    } else if (shapeType === 'line') {
      // For lines, pass center coordinates directly (following Konva best practices)
      shapeX = x;
      shapeY = y;
    } else {
      // For rectangles, center them
      shapeX = x - width / 2;
      shapeY = y - height / 2;
    }
    
    const newShape = {
      id: `shape-${Date.now()}`,
      x: shapeX,
      y: shapeY,
      type: "shape",
      shapeType: shapeType,
      width: width,
      height: height,
      fill: "#ffffff",
      stroke: "#000000",
      strokeWidth: 2
    };
    updateCurrentPanelContent('shapes', [...(current.shapes || []), newShape]);
    setSelectedId(newShape.id);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const assetData = e.dataTransfer.getData("application/json");
    if (assetData) {
      const asset = JSON.parse(assetData);
      const stage = e.target.getStage();
      const point = stage.getPointerPosition();
      const newId = `${asset.id}_${Date.now()}`;
      const newNode = {
        id: newId,
        src: asset.src,
        x: point.x - 25,
        y: point.y - 25,
        type: "asset",
        assetType: asset.assetType || "fullBody",
        scaling: asset.scaling || 50,
        flipped: false
      };
      const current = getCurrentPanelContent();
      updateCurrentPanelContent('nodes', [...(current.nodes || []), newNode]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleUpdateElement = (id, property, value) => {
    const current = getCurrentPanelContent();

    // Update nodes
    const nodeIndex = (current.nodes || []).findIndex(n => n.id === id);
    if (nodeIndex !== -1) {
      const newNodes = [...current.nodes];
      newNodes[nodeIndex] = { ...newNodes[nodeIndex], [property]: value };
      updateCurrentPanelContent('nodes', newNodes);
    }

    // Update speech bubbles
    const bubbleIndex = (current.speechBubbles || []).findIndex(b => b.id === id);
    if (bubbleIndex !== -1) {
      const newBubbles = [...current.speechBubbles];
      newBubbles[bubbleIndex] = { ...newBubbles[bubbleIndex], [property]: value };
      updateCurrentPanelContent('speechBubbles', newBubbles);
    }

    // Update text elements
    const textIndex = (current.textElements || []).findIndex(t => t.id === id);
    if (textIndex !== -1) {
      const newTexts = [...current.textElements];
      newTexts[textIndex] = { ...newTexts[textIndex], [property]: value };
      updateCurrentPanelContent('textElements', newTexts);
    }

    // Update shapes
    const shapeIndex = (current.shapes || []).findIndex(s => s.id === id);
    if (shapeIndex !== -1) {
      const newShapes = [...current.shapes];
      newShapes[shapeIndex] = { ...newShapes[shapeIndex], [property]: value };
      updateCurrentPanelContent('shapes', newShapes);
    }

    // Update strokes (free drawings)
    const strokeIndex = (current.strokes || []).findIndex(s => s.id === id);
    if (strokeIndex !== -1) {
      const newStrokes = [...current.strokes];
      const stroke = newStrokes[strokeIndex];
      const pts = Array.isArray(stroke.points) ? stroke.points : [];
      if (pts.length > 0) {
        const xs = pts.map(p => p[0]);
        const ys = pts.map(p => p[1]);
        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);
        const width = Math.max(0.0001, maxX - minX);
        const height = Math.max(0.0001, maxY - minY);
        let updatedPoints = pts;

        if (property === 'x') {
          const dx = (Number(value) || 0) - minX;
          updatedPoints = pts.map(([x, y]) => [x + dx, y]);
        } else if (property === 'y') {
          const dy = (Number(value) || 0) - minY;
          updatedPoints = pts.map(([x, y]) => [x, y + dy]);
        } else if (property === 'width') {
          const targetW = Math.max(0.0001, Number(value) || width);
          const scaleX = targetW / width;
          const cx = (minX + maxX) / 2;
          updatedPoints = pts.map(([x, y]) => [cx + (x - cx) * scaleX, y]);
        } else if (property === 'height') {
          const targetH = Math.max(0.0001, Number(value) || height);
          const scaleY = targetH / height;
          const cy = (minY + maxY) / 2;
          updatedPoints = pts.map(([x, y]) => [x, cy + (y - cy) * scaleY]);
        } else if (property === 'strokeWidth' || property === 'strokeColor' || property === 'opacity') {
          newStrokes[strokeIndex] = { ...stroke, [property]: value };
          updateCurrentPanelContent('strokes', newStrokes);
          return;
        } else {
          newStrokes[strokeIndex] = { ...stroke, [property]: value };
          updateCurrentPanelContent('strokes', newStrokes);
          return;
        }

        const newPath = generateSmoothPath(updatedPoints, stroke.smoothness ?? 0);
        newStrokes[strokeIndex] = { ...stroke, points: updatedPoints, pathData: newPath };
        updateCurrentPanelContent('strokes', newStrokes);
      }
    }
  };

  const handleCopyElement = () => {
    if (!selectedElement) return;

    if (selectedElement.type === "speechBubble") {
      const newId = `bubble_${Date.now()}`;
      const newBubble = {
        ...selectedElement,
        id: newId,
        x: selectedElement.x + 20,
        y: selectedElement.y + 20,
      };
      setSpeechBubbles([...speechBubbles, newBubble]);
      setSelectedId(newId);
    } else if (selectedElement.type === "text") {
      const newId = `text_${Date.now()}`;
      const newText = {
        ...selectedElement,
        id: newId,
        x: selectedElement.x + 20,
        y: selectedElement.y + 20,
      };
      setTextElements([...textElements, newText]);
      setSelectedId(newId);
    } else if (selectedElement.type === "shape") {
      const newId = `shape_${Date.now()}`;
      const newShape = {
        ...selectedElement,
        id: newId,
        x: selectedElement.x + 20,
        y: selectedElement.y + 20,
      };
      setShapes([...shapes, newShape]);
      setSelectedId(newId);
    } else if (selectedElement.type === "drawing") {
      const newId = `stroke_${Date.now()}`;
      const newStroke = {
        ...selectedElement,
        id: newId,
        x: selectedElement.x + 20,
        y: selectedElement.y + 20,
      };
      setStrokes([...strokes, newStroke]);
      setSelectedId(newId);
    } else {
      const newId = `${selectedElement.id}_copy_${Date.now()}`;
      const newNode = {
        ...selectedElement,
        id: newId,
        x: selectedElement.x + 20,
        y: selectedElement.y + 20,
      };
      setNodes([...nodes, newNode]);
      setSelectedId(newId);
    }
  };



  const handleBringToFront = () => {
    if (!selectedElement) return;
    if (selectedElement.type === "speechBubble") {
      const idx = speechBubbles.findIndex(b => b.id === selectedElement.id);
      if (idx !== -1) {
        const updated = [...speechBubbles];
        const [item] = updated.splice(idx, 1);
        updated.push(item);
        setSpeechBubbles(updated);
      }
    } else if (selectedElement.type === "text") {
      const idx = textElements.findIndex(t => t.id === selectedElement.id);
      if (idx !== -1) {
        const updated = [...textElements];
        const [item] = updated.splice(idx, 1);
        updated.push(item);
        setTextElements(updated);
      }
    } else if (selectedElement.type === "shape") {
      const idx = shapes.findIndex(s => s.id === selectedElement.id);
      if (idx !== -1) {
        const updated = [...shapes];
        const [item] = updated.splice(idx, 1);
        updated.push(item);
        setShapes(updated);
      }
    } else if (selectedElement.type === "drawing") {
      const idx = strokes.findIndex(s => s.id === selectedElement.id);
      if (idx !== -1) {
        const updated = [...strokes];
        const [item] = updated.splice(idx, 1);
        updated.push(item);
        setStrokes(updated);
      }
    } else {
      const idx = nodes.findIndex(n => n.id === selectedElement.id);
      if (idx !== -1) {
        const updated = [...nodes];
        const [item] = updated.splice(idx, 1);
        updated.push(item);
        setNodes(updated);
      }
    }
  };

  const handleSendToBack = () => {
    if (!selectedElement) return;
    if (selectedElement.type === "speechBubble") {
      const idx = speechBubbles.findIndex(b => b.id === selectedElement.id);
      if (idx !== -1) {
        const updated = [...speechBubbles];
        const [item] = updated.splice(idx, 1);
        updated.unshift(item);
        setSpeechBubbles(updated);
      }
    } else if (selectedElement.type === "text") {
      const idx = textElements.findIndex(t => t.id === selectedElement.id);
      if (idx !== -1) {
        const updated = [...textElements];
        const [item] = updated.splice(idx, 1);
        updated.unshift(item);
        setTextElements(updated);
      }
    } else if (selectedElement.type === "shape") {
      const idx = shapes.findIndex(s => s.id === selectedElement.id);
      if (idx !== -1) {
        const updated = [...shapes];
        const [item] = updated.splice(idx, 1);
        updated.unshift(item);
        setShapes(updated);
      }
    } else if (selectedElement.type === "drawing") {
      const idx = strokes.findIndex(s => s.id === selectedElement.id);
      if (idx !== -1) {
        const updated = [...strokes];
        const [item] = updated.splice(idx, 1);
        updated.unshift(item);
        setStrokes(updated);
      }
    } else {
      const idx = nodes.findIndex(n => n.id === selectedElement.id);
      if (idx !== -1) {
        const updated = [...nodes];
        const [item] = updated.splice(idx, 1);
        updated.unshift(item);
        setNodes(updated);
      }
    }
  };

  const handleFlipHorizontal = () => {
    if (!selectedElement) return;
    const current = panels[currentPanelIndex] || { nodes: [], speechBubbles: [], textElements: [], shapes: [], strokes: [] };

    if (selectedElement.type === "speechBubble") {
      const idx = (current.speechBubbles || []).findIndex(b => b.id === selectedElement.id);
      if (idx !== -1) {
        const updated = [...current.speechBubbles];
        updated[idx] = { ...updated[idx], flipped: !updated[idx].flipped };
        updateCurrentPanelContent('speechBubbles', updated);
      }
    } else if (selectedElement.type === "shape") {
      const idx = (current.shapes || []).findIndex(s => s.id === selectedElement.id);
      if (idx !== -1) {
        const updated = [...current.shapes];
        const shape = updated[idx];
        if (shape.shapeType === 'circle') {
          // For circles/ellipses, use scaleX: -1 method (Konva best practice)
          const currentScaleX = shape.scaleX || 1;
          updated[idx] = { ...shape, scaleX: -currentScaleX };
        } else if (shape.shapeType === 'line') {
          // For lines, use scaleX: -1 method (Konva best practice)
          const currentScaleX = shape.scaleX || 1;
          updated[idx] = { ...shape, scaleX: -currentScaleX };
        } else {
          // For rectangles, use scaleX: -1 to flip about center (Konva best practice)
          const currentScaleX = shape.scaleX || 1;
          updated[idx] = { ...shape, scaleX: -currentScaleX };
        }
        updateCurrentPanelContent('shapes', updated);
      }
    } else if (selectedElement.type === "drawing") {
      const idx = (current.strokes || []).findIndex(s => s.id === selectedElement.id);
      if (idx !== -1) {
        const updated = [...current.strokes];
        const stroke = updated[idx];
        const pts = Array.isArray(stroke.points) ? stroke.points : [];
        if (pts.length > 0) {
          const xs = pts.map(p => p[0]);
          const minX = Math.min(...xs);
          const maxX = Math.max(...xs);
          const centerX = (minX + maxX) / 2;
          const flippedPoints = pts.map(p => [2 * centerX - p[0], p[1]]);
          const newPathData = generateSmoothPath(flippedPoints, stroke.smoothness ?? 0);
          updated[idx] = { ...stroke, points: flippedPoints, pathData: newPathData, flipped: !stroke.flipped };
          updateCurrentPanelContent('strokes', updated);
        }
      }
    } else {
      const idx = (current.nodes || []).findIndex(n => n.id === selectedElement.id);
      if (idx !== -1) {
        const updated = [...current.nodes];
        updated[idx] = { ...updated[idx], flipped: !updated[idx].flipped };
        updateCurrentPanelContent('nodes', updated);
      }
    }
  };

  const handleExport = () => {
    if (stageRef.current) {
      // Unselect all elements before export to avoid showing selection handles
      setSelectedId(null);
      
      // Small delay to ensure the unselect renders before capture
      setTimeout(() => {
        const dataURL = stageRef.current.toDataURL({
          pixelRatio: 2, // Higher quality export
          mimeType: 'image/png',
          quality: 1
        });
        
        // Create a download link
        const link = document.createElement('a');
        link.download = 'comic-creator-export.png';
        link.href = dataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, 50);
    }
  };

  const handleExportStrip = () => {
    // Temporarily switch to strip view and capture the full composite
    const previousView = viewMode;
    const previousDrawing = drawingMode;
    setSelectedId(null);
    if (previousDrawing) setDrawingMode(false);
    if (previousView !== 'strip') setViewMode('strip');

    // Wait for stage to resize/re-render, then add extra delay for image loading
    setTimeout(() => {
      // Additional 100ms delay to ensure all images are fully rendered
      setTimeout(() => {
        if (stageRef.current) {
          const dataURL = stageRef.current.toDataURL({
            pixelRatio: 2,
            mimeType: 'image/png',
            quality: 1
          });
          const link = document.createElement('a');
          link.download = 'comic-strip-export.png';
          link.href = dataURL;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        // Restore previous UI state
        if (previousView !== 'strip') setViewMode(previousView);
        if (previousDrawing) setDrawingMode(true);
      }, 100);
    }, 0);
  };

  const handleSaveScene = () => {
    const sceneData = {
      version: "1.0",
      timestamp: new Date().toISOString(),
      canvasDimensions,
      nodes,
      speechBubbles,
      textElements,
      shapes,
      strokes,
      panels
    };

    const dataStr = JSON.stringify(sceneData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    const link = document.createElement('a');
    link.download = `comic-scene-${new Date().toISOString().split('T')[0]}.json`;
    link.href = URL.createObjectURL(dataBlob);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const handleLoadScene = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const sceneData = JSON.parse(e.target.result);
            
            // Validate the scene data structure
            if (sceneData.version && sceneData.canvasDimensions) {
              // Load the scene data
              setCanvasDimensions(sceneData.canvasDimensions);
              
              // Handle old format (no panels) vs new format (with panels)
              if (sceneData.panels && sceneData.panels.length > 0) {
                // New format with panels
                setPanels(sceneData.panels);
                setCurrentPanelIndex(0);
              } else {
                // Old format - create a default panel with existing content
                const defaultPanel = {
                  id: `panel_${Date.now()}_0`,
                  index: 0,
                  label: 'Panel 1',
                  layout: '1x1',
                  type: 'panel',
                  nodes: sceneData.nodes || [],
                  speechBubbles: sceneData.speechBubbles || [],
                  textElements: sceneData.textElements || [],
                  shapes: sceneData.shapes || [],
                  strokes: sceneData.strokes || []
                };
                setPanels([defaultPanel]);
                setCurrentPanelIndex(0);
              }
              
              setSelectedId(null); // Clear selection
              
              console.log('Scene loaded successfully:', sceneData);
            } else {
              alert('Invalid scene file format. Please select a valid comic scene file.');
            }
          } catch (error) {
            console.error('Error loading scene:', error);
            alert('Error loading scene file. Please check the file format.');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  // Load default scene on app startup
  const loadDefaultScene = useCallback(async () => {
    try {
      const response = await fetch(`/default-scene.json?t=${Date.now()}`);
      if (response.ok) {
        const sceneData = await response.json();
        
        // Validate the scene data structure
        if (sceneData.version && (sceneData.canvas || sceneData.canvasDimensions)) {
          // Load the scene data
          setCanvasDimensions(sceneData.canvas || sceneData.canvasDimensions);
          
          // Handle old format (no panels) vs new format (with panels)
          if (sceneData.panels && sceneData.panels.length > 0) {
            // New format with panels
            setPanels(sceneData.panels);
            setCurrentPanelIndex(0);
          } else {
            // Old format - create a default panel with existing content
            const defaultPanel = {
              id: `panel_${Date.now()}_0`,
              index: 0,
              label: 'Panel 1',
              layout: '1x1',
              type: 'panel',
              nodes: sceneData.nodes || [],
              speechBubbles: sceneData.speechBubbles || [],
              textElements: sceneData.textElements || [],
              shapes: sceneData.shapes || [],
              strokes: sceneData.strokes || []
            };
            setPanels([defaultPanel]);
            setCurrentPanelIndex(0);
          }
          
          setSelectedId(null); // Clear selection
          
          console.log('Default scene loaded successfully:', sceneData);
        } else {
          console.log('No valid default scene found, using empty canvas');
        }
      } else {
        console.log('No default scene file found, using empty canvas');
      }
    } catch (error) {
      console.log('Error loading default scene:', error);
      // Continue with empty canvas - this is not a critical error
    }
  }, [setCanvasDimensions, setSelectedId]);

  // Load default scene on app startup
  React.useEffect(() => {
    loadDefaultScene();
  }, [loadDefaultScene]);

  const exitDrawingMode = useCallback(() => {
    if (isDrawing) {
      setIsDrawing(false);
      setCurrentStroke(null);
      setDrawingPoints([]);
    }
    if (drawingMode) setDrawingMode(false);
  }, [isDrawing, drawingMode]);

  // Exit drawing mode or placement mode on Escape key
  React.useEffect(() => {
    const onKeyDown = (evt) => {
      if (evt.key === 'Escape') {
        if (placementMode) {
          setPlacementMode(null);
          setPlacementData(null);
        } else {
          exitDrawingMode();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [drawingMode, isDrawing, exitDrawingMode, placementMode]);

  const handleClearScene = () => {
    if (window.confirm('Are you sure you want to clear the current scene? This action cannot be undone.')) {
      setNodes([]);
      setSpeechBubbles([]);
      setTextElements([]);
      setShapes([]);
      setStrokes([]);
      setSelectedId(null);
      setCurrentPanelIndex(0);
      
      // Create a default empty panel so users can immediately add assets
      const defaultPanel = {
        id: `panel_${Date.now()}_0`,
        index: 0,
        label: 'Panel 1',
        layout: '1x1',
        type: 'panel',
        nodes: [],
        speechBubbles: [],
        textElements: [],
        shapes: [],
        strokes: []
      };
      setPanels([defaultPanel]);
    }
  };

  // Handle placement click - place the object at mouse position
  const handlePlacementClick = (e) => {
    if (!placementMode) return;
    
    const stage = e.target.getStage();
    const pointerPosition = stage.getPointerPosition();
    
    if (placementMode === 'asset') {
      handleAddAssetAtPosition(placementData, pointerPosition.x, pointerPosition.y);
    } else if (placementMode === 'shape') {
      handleAddShapeAtPosition(placementData, pointerPosition.x, pointerPosition.y);
    } else if (placementMode === 'text') {
      handleAddTextAtPosition(pointerPosition.x, pointerPosition.y);
    }
    
    // Exit placement mode
    setPlacementMode(null);
    setPlacementData(null);
  };

  // Panel management functions - page-based pages and navigation
  const handleAddPage = (position = 'after') => {
    const insertAt = position === 'before' ? currentPanelIndex : currentPanelIndex + 1;
    const current = panels[currentPanelIndex] || { nodes: [], speechBubbles: [], textElements: [], shapes: [], strokes: [] };
    const clone = (obj) => JSON.parse(JSON.stringify(obj));
    const newPanel = {
      id: `panel_${Date.now()}`,
      label: 'Panel',
      type: 'panel',
      nodes: duplicateOnAdd ? clone(current.nodes || []) : [],
      speechBubbles: duplicateOnAdd ? clone(current.speechBubbles || []) : [],
      textElements: duplicateOnAdd ? clone(current.textElements || []) : [],
      shapes: duplicateOnAdd ? clone(current.shapes || []) : [],
      strokes: duplicateOnAdd ? clone(current.strokes || []) : []
    };

    const updated = [...panels];
    updated.splice(insertAt, 0, newPanel);
    // Relabel panels sequentially for clarity
    const relabeled = updated.map((p, idx) => ({ ...p, label: `Panel ${idx + 1}` }));
    setPanels(relabeled);
    setCurrentPanelIndex(insertAt);
  };

  const handleSelectPanel = (panelIndex) => {
    setCurrentPanelIndex(panelIndex);
    setSelectedId(null); // Clear element selection when switching panels
  };

  const handleDeletePanel = (panelIndex) => {
    if (panels.length <= 1) {
      alert('Cannot delete the last panel. Create a new panel layout first.');
      return;
    }

    if (window.confirm('Are you sure you want to delete this panel? All content in this panel will be deleted.')) {
      const newPanels = panels.filter((_, index) => index !== panelIndex);
      setPanels(newPanels);
      
      // Adjust current panel index
      if (currentPanelIndex >= newPanels.length) {
        setCurrentPanelIndex(newPanels.length - 1);
      }
    }
  };

  const handleNextPanel = () => {
    if (currentPanelIndex < panels.length - 1) {
      setCurrentPanelIndex(currentPanelIndex + 1);
    }
  };

  const handlePreviousPanel = () => {
    if (currentPanelIndex > 0) {
      setCurrentPanelIndex(currentPanelIndex - 1);
    }
  };

  const handleToggleViewMode = () => {
    if (viewMode === 'panel') {
      if (drawingMode) setDrawingMode(false);
      setSelectedId(null);
      // Ensure any pan state is cleared before entering preview
      if (isPanning) setIsPanning(false);
      setViewMode('strip');
    } else {
      // Leaving preview: immediately clear pan state so cursor updates
      if (isPanning) setIsPanning(false);
      setViewMode('panel');
    }
  };

  // (moved earlier)

  // Get all content for strip view
  // Note: getAllContent helper removed (unused)

  const handleReloadDefaultScene = () => {
    if (window.confirm('Are you sure you want to reload the default scene? This will replace your current scene.')) {
      loadDefaultScene();
    }
  };

  const handleCanvasDimensionChange = (newDimensions) => {
    setCanvasDimensions(newDimensions);
  };

  const handleCanvasWheel = (e) => {
    // Handle Shift + Mouse Wheel for horizontal scrolling
    if (e.shiftKey) {
      e.preventDefault();
      const container = e.currentTarget;
      container.scrollLeft += e.deltaY;
    }
    // Normal vertical scrolling is handled automatically by the browser
  };

  // Drawing tool functions
  const handleToggleDrawingMode = () => {
    const next = !drawingMode;
    setDrawingMode(next);
    if (next) {
      setSelectedId(null); // Clear selection when entering drawing mode
    }
  };

  const handleDrawingSettingsChange = (property, value) => {
    setDrawingSettings(prev => ({
      ...prev,
      [property]: value
    }));
  };

  // Drawing mouse event handlers
  const handleDrawingMouseDown = (e) => {
    const domEvt = e && e.evt;
    if (domEvt && typeof domEvt.preventDefault === 'function') domEvt.preventDefault();
    if (domEvt && typeof domEvt.stopPropagation === 'function') domEvt.stopPropagation();
    const stage = e.target.getStage();
    const point = stage.getPointerPosition();
    if (!point) return;
    setIsDrawing(true);
    const newPoints = [[point.x, point.y]];
    setDrawingPoints(newPoints);
    
    // Create initial stroke data
    const strokeData = {
      id: `stroke_${Date.now()}`,
      points: newPoints,
      pathData: generateSmoothPath(newPoints, drawingSettings.smoothness),
      strokeWidth: drawingSettings.strokeWidth,
      strokeColor: drawingSettings.strokeColor,
      opacity: drawingSettings.opacity,
      smoothness: drawingSettings.smoothness,
      type: 'drawing'
    };
    setCurrentStroke(strokeData);
  };

  const handleDrawingMouseMove = (e) => {
    if (!isDrawing || !drawingMode) return;
    
    const domEvt = e && e.evt;
    if (domEvt && typeof domEvt.preventDefault === 'function') domEvt.preventDefault();
    if (domEvt && typeof domEvt.stopPropagation === 'function') domEvt.stopPropagation();
    const stage = e.target.getStage();
    const point = stage.getPointerPosition();
    if (!point) return;
    const newPoints = [...drawingPoints, [point.x, point.y]];
    setDrawingPoints(newPoints);
    
    // Update current stroke
    const updatedStroke = {
      ...currentStroke,
      points: newPoints,
      pathData: generateSmoothPath(newPoints, drawingSettings.smoothness)
    };
    setCurrentStroke(updatedStroke);
  };

  const handleDrawingMouseUp = (e) => {
    if (!isDrawing || !drawingMode) return;
    
    const domEvt = e && e.evt;
    if (domEvt && typeof domEvt.preventDefault === 'function') domEvt.preventDefault();
    if (domEvt && typeof domEvt.stopPropagation === 'function') domEvt.stopPropagation();
    setIsDrawing(false);
    
    // Complete the stroke
    if (currentStroke) {
      const current = getCurrentPanelContent();
      const updated = [...(current.strokes || []), currentStroke];
      updateCurrentPanelContent('strokes', updated);
    }
    
    // Reset state
    setDrawingPoints([]);
    setCurrentStroke(null);
  };

  // Generate smooth path from points (local wrappers call module-scope util)
  // kept here to avoid large refactors of call sites

  return (
    <div className="app-container">
      <Toolbar 
        selectedElement={selectedElement}
        onCopy={(...a) => { if (drawingMode) exitDrawingMode(); handleCopyElement(...a); }}
        onDelete={(...a) => { if (drawingMode) exitDrawingMode(); handleDeleteElement(...a); }}
        onFlipHorizontal={(...a) => { if (drawingMode) exitDrawingMode(); handleFlipHorizontal(...a); }}
        onExport={(...a) => { if (drawingMode) exitDrawingMode(); handleExport(...a); }}
        onAddText={(...a) => { if (drawingMode) exitDrawingMode(); handleAddText(...a); }}
        onAddShape={(...a) => { if (drawingMode) exitDrawingMode(); handleAddShape(...a); }}
        onBringToFront={(...a) => { if (drawingMode) exitDrawingMode(); handleBringToFront(...a); }}
        onSendToBack={(...a) => { if (drawingMode) exitDrawingMode(); handleSendToBack(...a); }}
        canvasDimensions={canvasDimensions}
        onCanvasDimensionChange={(...a) => { if (drawingMode) exitDrawingMode(); handleCanvasDimensionChange(...a); }}
        onSaveScene={(...a) => { if (drawingMode) exitDrawingMode(); handleSaveScene(...a); }}
        onLoadScene={(...a) => { if (drawingMode) exitDrawingMode(); handleLoadScene(...a); }}
        onClearScene={(...a) => { if (drawingMode) exitDrawingMode(); handleClearScene(...a); }}
        onReloadDefaultScene={(...a) => { if (drawingMode) exitDrawingMode(); handleReloadDefaultScene(...a); }}
        drawingMode={drawingMode}
        onToggleDrawingMode={handleToggleDrawingMode}
        panels={panels}
        currentPanelIndex={currentPanelIndex}
        viewMode={viewMode}
        onSelectPanel={handleSelectPanel}
        onDeletePanel={handleDeletePanel}
        onNextPanel={handleNextPanel}
        onPreviousPanel={handlePreviousPanel}
        onToggleViewMode={handleToggleViewMode}
        onAddPageBefore={() => handleAddPage('before')}
        onAddPageAfter={() => handleAddPage('after')}
        duplicateOnAdd={duplicateOnAdd}
        onToggleDuplicateOnAdd={() => setDuplicateOnAdd(v => !v)}
        onExportStrip={handleExportStrip}
        onUploadImage={handleUploadImage}
      />
      <div className="panels-container">
        {viewMode !== 'strip' && (
          <AssetPanel onAddAsset={handleAddAsset} />
        )}
        <div className="main-content">
          <div className="canvas-container" onWheel={handleCanvasWheel} ref={canvasContainerRef}>
            <Stage
              style={{ 
                border: "1px solid #aaa",
                cursor: viewMode === 'strip' ? (isPanning ? 'grabbing' : 'grab') : (drawingMode ? 'crosshair' : (placementMode ? 'crosshair' : 'default'))
              }}
              onMouseDown={(e) => {
                if (viewMode === 'strip') { handleStripPanDown(e); return; }
                const isStageClick = e.target === e.target.getStage();
                if (drawingMode) {
                  handleDrawingMouseDown(e);
                  return; // suppress selection changes while drawing
                }
                if (placementMode) {
                  // In placement mode, always handle the click for placement, regardless of what was clicked
                  handlePlacementClick(e);
                  return;
                }
                if (isStageClick) {
                  setSelectedId(null);
                }
              }}
              onMouseMove={(e) => {
                if (viewMode === 'strip') { handleStripPanMove(e); return; }
                if (drawingMode) handleDrawingMouseMove(e);
                
                // Track mouse position for placement mode
                if (placementMode) {
                  const stage = e.target.getStage();
                  const pointerPosition = stage.getPointerPosition();
                  setMousePosition(pointerPosition);
                }
              }}
              onMouseUp={(e) => {
                if (viewMode === 'strip') { handleStripPanUp(); return; }
                if (drawingMode) handleDrawingMouseUp(e);
              }}
              onDrop={(e) => {
                if (drawingMode) return; // ignore drops while drawing
                handleDrop(e);
              }}
              onDragOver={(e) => {
                if (drawingMode) return; // ignore drag-over while drawing
                handleDragOver(e);
              }}
              ref={stageRef}
              width={(() => {
                if (viewMode !== 'strip') return canvasDimensions.width;
                const rows = Math.max(1, stripRows || 1);
                const cols = Math.max(1, Math.ceil((panels.length || 1) / rows));
                const stripPadding = 16;
                return cols * canvasDimensions.width + (cols + 1) * stripPadding;
              })()}
              height={(() => {
                if (viewMode !== 'strip') return canvasDimensions.height;
                const rows = Math.max(1, stripRows || 1);
                const stripPadding = 16;
                return rows * canvasDimensions.height + (rows + 1) * stripPadding;
              })()}
            >
              {/* Compute composite dimensions for strip view */}
              {(() => {
                const rows = Math.max(1, stripRows || 1);
                const cols = viewMode === 'strip' ? Math.max(1, Math.ceil((panels.length || 1) / rows)) : 1;
                const stripPadding = 16; // pixels between/outside panels
                const stageWidth = viewMode === 'strip'
                  ? cols * canvasDimensions.width + (cols + 1) * stripPadding
                  : canvasDimensions.width;
                const stageHeight = viewMode === 'strip'
                  ? rows * canvasDimensions.height + (rows + 1) * stripPadding
                  : canvasDimensions.height;
                return (
                  <Layer>
                    {/* White background - non-interactive so clicks pass through to stage */}
                    <Rect x={0} y={0} width={stageWidth} height={stageHeight} fill="white" listening={false} />
                
                {/* Render content based on view mode */}
                {viewMode === 'panel' ? (
                  // Panel view - show current panel content
                  (() => {
                    const currentContent = getCurrentPanelContent();
                    return (
                      <>
                        {currentContent.nodes.map((n, i) => (
                          <Asset
                            key={n.id}
                            id={n.id}
                            src={n.src}
                            x={n.x}
                            y={n.y}
                            centered={n.centered}
                            width={n.width}
                            height={n.height}
                            flipped={n.flipped}
                            scaling={n.scaling || 50}
                            canvasHeight={canvasDimensions.height}
                            type={n.type || "asset"}
                            rotation={n.rotation || 0}
                            isSelected={selectedId === n.id}
                            onSelect={() => {
                              if (drawingMode) return;
                              setSelectedId(n.id);
                            }}
                            onChange={(attrs) => {
                              const newNodes = [...currentContent.nodes];
                              newNodes[i] = { ...newNodes[i], ...attrs };
                              updateCurrentPanelContent('nodes', newNodes);
                            }}
                          />
                        ))}
                        {currentContent.speechBubbles.map((bubble, i) => (
                          <SpeechBubble
                            key={bubble.id}
                            {...bubble}
                            drawingMode={drawingMode}
                            selected={selectedId === bubble.id}
                            onSelect={() => {
                              if (drawingMode) return;
                              setSelectedId(bubble.id);
                            }}
                            onChange={(attrs) => {
                              const newBubbles = [...currentContent.speechBubbles];
                              newBubbles[i] = { ...newBubbles[i], ...attrs };
                              updateCurrentPanelContent('speechBubbles', newBubbles);
                            }}
                          />
                        ))}
                        {currentContent.textElements.map((text, i) => (
                          <TextElement
                            key={text.id}
                            {...text}
                            drawingMode={drawingMode}
                            isSelected={selectedId === text.id}
                            onSelect={() => {
                              if (drawingMode) return;
                              setSelectedId(text.id);
                            }}
                            onChange={(attrs) => {
                              const newTexts = [...currentContent.textElements];
                              newTexts[i] = { ...newTexts[i], ...attrs };
                              updateCurrentPanelContent('textElements', newTexts);
                            }}
                          />
                        ))}
                        {currentContent.shapes.map((shape, i) => (
                          <ShapeElement
                            key={shape.id}
                            {...shape}
                            drawingMode={drawingMode}
                            isSelected={selectedId === shape.id}
                            onSelect={() => {
                              if (drawingMode) return;
                              setSelectedId(shape.id);
                            }}
                            onChange={(attrs) => {
                              const newShapes = [...currentContent.shapes];
                              newShapes[i] = { ...newShapes[i], ...attrs };
                              updateCurrentPanelContent('shapes', newShapes);
                            }}
                          />
                        ))}
                        {(currentContent.strokes || []).map((stroke, i) => (
                          <StrokeElement
                            key={stroke.id}
                            {...stroke}
                            isSelected={selectedId === stroke.id}
                            onSelect={() => {
                              if (drawingMode) return;
                              setSelectedId(stroke.id);
                            }}
                            isDraggable={!drawingMode}
                            onChange={(attrs) => {
                              const newStrokes = [...(currentContent.strokes || [])];
                              const existing = newStrokes[i];
                              if (Object.prototype.hasOwnProperty.call(attrs, 'x') || Object.prototype.hasOwnProperty.call(attrs, 'y')) {
                                const offsetX = attrs.x || 0;
                                const offsetY = attrs.y || 0;
                                const existingPoints = Array.isArray(existing.points) ? existing.points : [];
                                const shiftedPoints = existingPoints.map((p) => [p[0] + offsetX, p[1] + offsetY]);
                                const newPath = generateSmoothPath(shiftedPoints, existing.smoothness ?? 0);
                                newStrokes[i] = { ...existing, points: shiftedPoints, pathData: newPath };
                              } else {
                                newStrokes[i] = { ...existing, ...attrs };
                              }
                              updateCurrentPanelContent('strokes', newStrokes);
                            }}
                          />
                        ))}
                        {/* Render current stroke being drawn */}
                        {isDrawing && currentStroke && currentStroke.pathData && (
                          <Path
                            data={currentStroke.pathData}
                            stroke={currentStroke.strokeColor}
                            strokeWidth={currentStroke.strokeWidth}
                            opacity={currentStroke.opacity}
                            lineCap="round"
                            lineJoin="round"
                            listening={false}
                          />
                        )}
                      </>
                    );
                  })()
                ) : (
                  // Strip view - show all panels in a grid by rows (left-to-right, top-to-bottom)
                  (() => {
                    const rows = Math.max(1, stripRows || 1);
                    const cols = Math.max(1, Math.ceil((panels.length || 1) / rows));
                    const stripPadding = 16;
                    // stageW/stageH were unused; using stageWidth/stageHeight instead
                    return (
                      <>
                        {panels.map((panel, index) => {
                          const row = Math.floor(index / cols);
                          const col = index % cols;
                          const offsetX = stripPadding + col * (canvasDimensions.width + stripPadding);
                          const offsetY = stripPadding + row * (canvasDimensions.height + stripPadding);
                          return (
                            <React.Fragment key={panel.id}>
                              {/* Panel outline */}
                              <Rect
                                x={offsetX}
                                y={offsetY}
                                width={canvasDimensions.width}
                                height={canvasDimensions.height}
                                fill="white"
                                stroke="#333333"
                                strokeWidth={2}
                                listening={false}
                              />
                              {(panel.nodes || []).map((n) => (
                                <Asset
                                  key={n.id}
                                  id={n.id}
                                  src={n.src}
                                  x={(n.x || 0) + offsetX}
                                  y={(n.y || 0) + offsetY}
                                  centered={n.centered}
                                  width={n.width}
                                  height={n.height}
                                  flipped={n.flipped}
                                  scaling={n.scaling || 50}
                                  canvasHeight={canvasDimensions.height}
                                  type={n.type || "asset"}
                                  rotation={n.rotation || 0}
                                  isSelected={false}
                                  onSelect={() => {}}
                                  onChange={() => {}}
                                />
                              ))}
                              {(panel.speechBubbles || []).map((bubble) => (
                                <SpeechBubble
                                  key={bubble.id}
                                  {...bubble}
                                  x={(bubble.x || 0) + offsetX}
                                  y={(bubble.y || 0) + offsetY}
                                  drawingMode={false}
                                  selected={false}
                                  onSelect={() => {}}
                                  onChange={() => {}}
                                />
                              ))}
                              {(panel.textElements || []).map((text) => (
                                <TextElement
                                  key={text.id}
                                  {...text}
                                  x={(text.x || 0) + offsetX}
                                  y={(text.y || 0) + offsetY}
                                  drawingMode={false}
                                  isSelected={false}
                                  onSelect={() => {}}
                                  onChange={() => {}}
                                />
                              ))}
                              {(panel.shapes || []).map((shape) => (
                                <ShapeElement
                                  key={shape.id}
                                  {...shape}
                                  x={(shape.x || 0) + offsetX}
                                  y={(shape.y || 0) + offsetY}
                                  drawingMode={false}
                                  isSelected={false}
                                  onSelect={() => {}}
                                  onChange={() => {}}
                                />
                              ))}
                              {(panel.strokes || []).map((stroke) => {
                                const shiftedPoints = (stroke.points || []).map(([x, y]) => [x + offsetX, y + offsetY]);
                                const shiftedPath = generateSmoothPath(shiftedPoints, stroke.smoothness ?? 0);
                                return (
                                  <StrokeElement
                                    key={stroke.id}
                                    {...stroke}
                                    isSelected={false}
                                    onSelect={() => {}}
                                    isDraggable={false}
                                    onChange={() => {}}
                                    points={shiftedPoints}
                                    pathData={shiftedPath}
                                  />
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                        {/* Top overlay to block all interactions in print preview */}
                        <Rect
                          x={0}
                          y={0}
                          width={stageWidth}
                          height={stageHeight}
                          fill={"rgba(0,0,0,0.001)"}
                          listening={true}
                        />
                      </>
                    );
                  })()
                )}
                {/* Disable drawing overlay in strip (print preview) mode */}
                {drawingMode && viewMode === 'panel' && (
                  <Rect
                    x={0}
                    y={0}
                    width={canvasDimensions.width}
                    height={canvasDimensions.height}
                    fill={"rgba(0,0,0,0.001)"}
                    listening={true}
                    onMouseDown={handleDrawingMouseDown}
                    onMouseMove={handleDrawingMouseMove}
                    onMouseUp={handleDrawingMouseUp}
                  />
                )}

                {/* Placement preview */}
                {placementMode && (
                  <PlacementPreview 
                    mode={placementMode}
                    data={placementData}
                    x={mousePosition.x}
                    y={mousePosition.y}
                    canvasDimensions={canvasDimensions}
                  />
                )}
                  </Layer>
                );
              })()}
            </Stage>
          </div>
        </div>
        {viewMode !== 'strip' && (
          <PropertiesPanel 
            selectedElement={selectedElement}
            onUpdateElement={handleUpdateElement}
          />
        )}
        <DrawingSettings
          settings={drawingSettings}
          onSettingsChange={handleDrawingSettingsChange}
          isVisible={drawingMode}
        />
        <ImageUploader
          onImageUpload={handleImageUpload}
          onClose={handleCloseUploadModal}
          isOpen={showUploadModal}
          canvasDimensions={canvasDimensions}
        />
      </div>
    </div>
  );
}
